import { Activity, Database, Github, Plug, Server, type LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils/cn'
import type { ComponentStatus } from '@/types/domain'

const DOT: Record<string, string> = {
  healthy: 'bg-ok',
  degraded: 'bg-waiting',
  unavailable: 'bg-fail',
  unknown: 'bg-ink-faint',
  not_configured: 'bg-ink-faint',
  // Neutral, not amber: an account that has not connected GitHub yet is the normal state
  // of a healthy server, and a cluster that is permanently warning is one nobody reads.
  not_connected: 'bg-ink-faint',
}

const STATE_WORD: Record<string, string> = {
  healthy: 'healthy',
  degraded: 'degraded',
  unavailable: 'down',
  unknown: 'unknown',
  not_configured: 'not set up',
  not_connected: 'not connected',
}

const ICON: Record<string, LucideIcon> = {
  backend: Server,
  database: Database,
  GitHub: Github,
  MCP: Plug,
}

const NAME: Record<string, string> = {
  backend: 'Backend',
  database: 'Database',
  GitHub: 'GitHub',
  MCP: 'MCP server',
}

/**
 * One component's health, as an icon with a status dot.
 *
 * The words are still here — for a screen reader, and on hover or keyboard focus — just not
 * spelled across the bar. A row of "backend healthy · database healthy" reads as an
 * operator's debug strip; an icon with a green dot is how a platform says "fine", and the
 * detail is one hover away for the moment it is not.
 *
 * Colour is never the only signal: the icon names the component, and the state word is in
 * the accessible name and the tooltip.
 */
export function StatusPill({
  label,
  status,
  loading,
}: {
  label: string
  status?: ComponentStatus
  loading?: boolean
}) {
  const state = status?.state ?? 'unknown'
  const word = STATE_WORD[state] ?? state
  const detail = status?.detail
  const Icon = ICON[label] ?? Activity

  return (
    <span
      // Focusable so the tooltip is reachable from the keyboard, not only under a pointer.
      tabIndex={0}
      className="group relative inline-flex h-8 w-8 items-center justify-center rounded-md border
        border-line bg-surface text-ink-muted transition-colors hover:bg-hover hover:text-ink
        focus-visible:text-ink"
    >
      <Icon aria-hidden="true" className="h-4 w-4" />
      <span
        aria-hidden="true"
        className={cn(
          'absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-surface',
          DOT[state] ?? DOT.unknown,
          loading && 'animate-pulse',
        )}
      />

      <span className="sr-only">{label}</span>
      <span className="sr-only">{loading ? '…' : word}</span>

      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-max
          max-w-[18rem] rounded-md border border-line bg-overlay px-2.5 py-1.5 text-left text-xs leading-snug text-ink
          shadow-pop group-hover:block group-focus-visible:block"
      >
        {/* One text node for the heading line, so the tooltip never repeats the exact words
            the accessible name already carries. */}
        <span className="flex items-center gap-1.5 font-medium">
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT[state] ?? DOT.unknown)} />
          {`${NAME[label] ?? label} — ${loading ? 'checking…' : word}`}
        </span>
        {detail && !loading ? (
          <span className="mt-1 block text-ink-muted" title={detail}>
            {detail}
          </span>
        ) : null}
      </span>
    </span>
  )
}
